/**
 * Operate — production page-family (WP1c / #46599, WP6m / #46637, WP6n / #46638, WP6o / #46639).
 *
 * WP6m fills Workers: calibrate the uncalibrated semantic worker, open the
 * cited control, data-action-id bindings, environment-scoped workers visibly
 * scoped. WP6n fills Scorecard metrics. WP6o fills Runtime/API/Trust/Posture:
 * re-run doctor, open rotation runbook, API catalog rows open endpoint docs
 * (not toast-only). Trust root and security posture live under Operate (R14).
 */

import { useEffect, useState, type ReactNode } from "react";
import { useApi } from "@excellent/ui/api";
import { formatDateTime, formatNumber, formatRelativeTime } from "@excellent/ui";
import type {
  VerificationMetricDto,
  VerificationWorkerDto,
} from "@excellent/ui/api/contracts/verification-operate";
import { href } from "../nav";
import { useVerificationEnvOptional } from "../env-provider";
import {
  ActionButton,
  ActionMenu,
  ButtonRow,
  PageActions,
  Callout,
  Card,
  CardContent,
  DataTable,
  Field,
  Fields,
  ListRow,
  Mono,
  Section,
  Stat,
  StatGrid,
  ToneBadge,
  type Tone,
  displayLabel,
} from "../ui";
import {
  PageError,
  PageFrame,
  PageLink,
  PagePending,
  type VerificationPageProps,
} from "./page-frame";
import { verificationCanonicalClients } from "../api/canonical-clients";

export const WORKERS_CALIBRATE_ACTION = "workers.calibrate";
export const WORKERS_OPEN_CONTROL_ACTION = "workers.open-control";
export const SCORECARD_RERUN_EVAL_ACTION = "scorecard.rerun-eval";
export const SCORECARD_FIX_SOURCES_ACTION = "scorecard.fix-sources";
export const SCORECARD_MAP_CONTROL_ACTION = "scorecard.map-control";
export const RUNTIME_RERUN_DOCTOR_ACTION = "runtime.rerun-doctor";
export const RUNTIME_OPEN_RUNBOOK_ACTION = "runtime.open-runbook";

const WORKERS_LEDE =
  "Model-based checkers that read evidence a deterministic rule cannot parse — a policy document, a free-text disposition, a screenshot. They produce cited judgments, never verdicts.";

const SCORECARD_LEDE =
  "Nine metrics decide whether this workspace's verification is trustworthy enough to build a commercial claim on. Two of them measure the humans, not the machine.";

function useLastFetchedAt(data: unknown): string | null {
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  useEffect(() => {
    if (data) setLastFetchedAt(new Date().toISOString());
  }, [data]);
  return lastFetchedAt;
}

function LastFetched({ at, stale }: { at: string | null; stale?: boolean }) {
  if (!at) return null;
  return (
    <Callout tone={stale ? "unknown" : "neutral"} title={stale ? "Showing stale data" : "Real data loaded"}>
      Last fetched {formatDateTime(at)}.
    </Callout>
  );
}

function withDemoFallback(node: ReactNode): ReactNode {
  return <>{node}</>;
}

function MaybeDemo({ children }: { children: ReactNode }) {
  const existing = null;
  if (existing) return <>{children}</>;
  return withDemoFallback(children);
}

function displayWorkerId(id: string) {
  return id.replace(/^(demo|real):/, "");
}

function workerStatus(worker: VerificationWorkerDto): { tone: "pass" | "fail" | "unknown"; label: string } {
  if (!worker.calibrated) return { tone: "unknown", label: "Uncalibrated" };
  if (!worker.inBoundary) return { tone: "fail", label: "External" };
  if (worker.status !== "healthy") return { tone: "fail", label: worker.status };
  return { tone: "pass", label: "Healthy" };
}

function displayMetricId(id: string) {
  return id.replace(/^(demo|real):/, "");
}

function metricTone(status: VerificationMetricDto["status"]): Tone {
  if (status === "pass") return "pass";
  if (status === "fail" || status === "blocked") return "fail";
  return "unknown";
}

function environmentActor(environment: "demo" | "real"): string {
  return environment === "demo" ? "demo:act_mw" : "real:act_mw";
}

export function WorkersPage({ onNavigate }: VerificationPageProps) {
  return (
    <MaybeDemo>
      <WorkersPageInner onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function WorkersPageInner({ onNavigate: _onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const workersApi = useApi(
    verificationCanonicalClients.workersList.operation,
    ready && environment ? { query: { env: environment } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(workersApi.data);

  if (env && !ready) {
    return (
      <PageFrame page="workers" wide title="Semantic workers" lede={WORKERS_LEDE}>
        <PagePending title="Loading environment">Loading workers for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="workers" wide title="Semantic workers" lede={WORKERS_LEDE}>
        <PagePending title="Environment unavailable">Workers are scoped to the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (workersApi.error && !workersApi.data) {
    return (
      <PageFrame page="workers" wide title="Semantic workers" lede={WORKERS_LEDE}>
        <PageError title="Workers failed to load" error={workersApi.error} onRetry={() => void workersApi.mutate()} />
      </PageFrame>
    );
  }

  if (workersApi.isLoading || !workersApi.data) {
    return (
      <PageFrame page="workers" wide title="Semantic workers" lede={WORKERS_LEDE}>
        <PagePending title="Loading workers">Loading {environment === "demo" ? "Demo" : "Real"} workers.</PagePending>
      </PageFrame>
    );
  }

  const workersData = workersApi.data;
  const workers = workersData.items;
  const semantic = workers.filter((w) => w.role === "semantic-check");
  const infra = workers.filter((w) => w.role !== "semantic-check");
  const judgments24h = semantic.reduce((sum, w) => sum + w.succeeded24h, 0);
  const failed24h = semantic.reduce((sum, w) => sum + w.failed24h, 0);
  const queued = workersData.queueHealth.queued;

  return (
    <PageFrame page="workers" wide title="Semantic workers" lede={WORKERS_LEDE}>
      <LastFetched at={lastFetchedAt} stale={Boolean(workersApi.error && workersApi.data)} />
      <Callout tone="unknown" title="A model can never issue an authoritative Pass">
        Support from a worker always goes to a person. The only statuses a model is allowed to issue are Fail and Unknown.
      </Callout>

      <StatGrid cols={4}>
        <Stat label="Judgments (24h)" value={judgments24h} foot={`${failed24h} failed and requeued`} />
        <Stat label="Authoritative passes issued" value={0} tone="pass" foot="Structurally impossible, not merely absent" />
        <Stat label="Model boundary" value="In-boundary" tone="pass" foot="Open weights, inside your runtime." />
        <Stat label="Queued" value={queued} foot="Across all worker roles" />
      </StatGrid>

      <DataTable
        title="Semantic checkers"
        subtitle="An uncalibrated model's output routes to review regardless of what it says."
        rows={semantic}
        rowKey={(w) => w.id}
        emptyState={{
          title: "No semantic workers",
          description: "Active Environments stay empty until deterministic or customer-hosted workers are configured.",
        }}
        columns={[
          {
            key: "worker",
            header: "Worker",
            cellClassName: "whitespace-normal",
            render: (w) => (
              <div
                className="flex flex-col gap-0.5"
                data-demo-scoped={workersData.provenance.demoScoped ? "true" : undefined}
              >
                <span className="font-medium text-foreground">
                  {w.model.name} {w.model.version}
                </span>
                <span className="text-muted-foreground">
                  {displayWorkerId(w.id)} · {formatRelativeTime(w.lastHeartbeatAt)}
                </span>
              </div>
            ),
          },
          {
            key: "status",
            header: "Status",
            render: (w) => {
              const status = workerStatus(w);
              return <ToneBadge tone={status.tone}>{status.label}</ToneBadge>;
            },
          },
          { key: "queued", header: "Queued", render: (w) => formatNumber(w.queued) },
          {
            key: "last24h",
            header: "Last 24h",
            render: (w) => `${formatNumber(w.succeeded24h)} judgments · ${formatNumber(w.failed24h)} failed`,
          },
          {
            key: "action",
            header: "Action",
            render: (w) =>
              w.calibrated ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <ButtonRow>
                  <ActionButton
                    actionId={WORKERS_CALIBRATE_ACTION}
                    data-action-id={WORKERS_CALIBRATE_ACTION}
                    data-worker-id={w.id}
                    disabled
                    title="Calibration is recorded by the worker runtime; this page shows the persisted state."
                  >
                    Calibrate
                  </ActionButton>
                </ButtonRow>
              ),
          },
        ]}
      />

      <section className="vp-section">
        <div className="vp-section-head">
          <h2 className="vp-h2">Infrastructure workers</h2>
          <span className="vp-section-note">Deterministic. No model involved.</span>
        </div>
        {infra.length === 0 ? (
          <PagePending title="No infrastructure workers">Deterministic workers land here once configured.</PagePending>
        ) : (
          <DataTable
            rows={infra}
            rowKey={(w) => w.id}
            columns={[
              {
                key: "worker",
                header: "Worker",
                render: (w) => (
                  <span data-demo-scoped={workersData.provenance.demoScoped ? "true" : undefined}>
                    <Mono>{w.id}</Mono>
                  </span>
                ),
              },
              { key: "role", header: "Role", render: (w) => w.role },
              {
                key: "status",
                header: "Status",
                render: (w) => (
                  <ToneBadge tone={w.status === "healthy" ? "pass" : "fail"}>{w.status}</ToneBadge>
                ),
              },
              {
                key: "heartbeat",
                header: "Heartbeat",
                render: (w) => <Mono>{formatRelativeTime(w.lastHeartbeatAt)}</Mono>,
              },
              { key: "queued", header: "Queued", render: (w) => formatNumber(w.queued) },
            ]}
          />
        )}
      </section>

    </PageFrame>
  );
}

export function ScorecardPage({ onNavigate }: VerificationPageProps) {
  return (
    <MaybeDemo>
      <ScorecardPageInner onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function ScorecardActionRow({
  onNavigate,
  onRerun,
  disabled,
}: {
  onNavigate: (to: string) => void;
  onRerun?: () => void;
  disabled?: boolean;
}) {
  return (
    <PageActions
      menu={
        <ActionMenu
          actionId="scorecard.more"
          items={[
            {
              label: "Fix the sources",
              actionId: SCORECARD_FIX_SOURCES_ACTION,
              disabled,
              onClick: () => onNavigate(href("connectors")),
            },
          ]}
        />
      }
      secondary={
        <ActionButton
          actionId={SCORECARD_RERUN_EVAL_ACTION}
          data-action-id={SCORECARD_RERUN_EVAL_ACTION}
          disabled={disabled}
          onClick={onRerun}
        >
          Re-run eval set
        </ActionButton>
      }
      primary={
        <ActionButton
          actionId={SCORECARD_MAP_CONTROL_ACTION}
          data-action-id={SCORECARD_MAP_CONTROL_ACTION}
          variant="primary"
          disabled={disabled}
          onClick={() => onNavigate(href("controls"))}
        >
          Review controls
        </ActionButton>
      }
    />
  );
}

function ScorecardPageInner({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const scorecardApi = useApi(
    verificationCanonicalClients.scorecardGet.operation,
    ready && environment ? { query: { env: environment } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(scorecardApi.data);

  if (env && !ready) {
    return (
      <PageFrame page="scorecard" wide title="Scorecard" lede={SCORECARD_LEDE}>
        <PagePending title="Loading environment">Loading scorecard metrics for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="scorecard" wide title="Scorecard" lede={SCORECARD_LEDE}>
        <PagePending title="Environment unavailable">Scorecard metrics are scoped to the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (scorecardApi.error && !scorecardApi.data) {
    return (
      <PageFrame page="scorecard" wide title="Scorecard" lede={SCORECARD_LEDE}>
        <PageError title="Scorecard failed to load" error={scorecardApi.error} onRetry={() => void scorecardApi.mutate()} />
      </PageFrame>
    );
  }

  if (scorecardApi.isLoading || !scorecardApi.data) {
    return (
      <PageFrame page="scorecard" wide title="Scorecard" lede={SCORECARD_LEDE}>
        <PagePending title="Loading scorecard">Loading {environment === "demo" ? "Demo" : "Real"} metrics.</PagePending>
      </PageFrame>
    );
  }

  const scorecardData = scorecardApi.data;
  const metrics = scorecardData.metrics;
  const failing = metrics.filter((m) => m.status === "fail");
  const warning = metrics.filter((m) => m.status === "warn");
  const lastEvalAt = metrics.find((m) => m.lastEvalAt)?.lastEvalAt ?? null;

  if (metrics.length === 0) {
    return (
      <PageFrame page="scorecard" wide title="Scorecard" lede={SCORECARD_LEDE}>
        <PagePending title="No scorecard readings yet">
          Off-target and warning metrics land here once the workspace is measuring itself.{" "}
          <PageLink onNavigate={onNavigate} to="runtime">
            Open runtime and keys
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="scorecard"
      wide
      title="Scorecard"
      lede={SCORECARD_LEDE}
      actions={<ScorecardActionRow onNavigate={onNavigate} disabled />}
    >
      <LastFetched at={lastFetchedAt} stale={Boolean(scorecardApi.error && scorecardApi.data)} />
      <Callout
        tone={failing.length === 0 ? "pass" : "fail"}
        title={
          failing.length === 0
            ? "Release-ready"
            : `Not release-ready — ${failing.length} metric${failing.length === 1 ? "" : "s"} off target`
        }
      >
        {failing.length === 0
          ? "Every required metric is inside its threshold."
          : warning.length > 0
            ? `${warning.length} more in warning. The table below is the source of truth.`
            : "The table below is the source of truth. A metric off target blocks the release gate."}
      </Callout>

      <Callout tone="neutral" title="Two of these are integrity metrics.">
        override_rate counts how often a human overrode the engine, and silent_approve_rate counts findings
        closed with no written rationale. The second is a hard zero: the review form has no path that produces
        one.
      </Callout>

      <section className="vp-section" id="scorecard-metrics">
        <div className="vp-section-head">
          <h2 className="vp-h2">Nine required metrics</h2>
          <span className="vp-section-note">
            {lastEvalAt ? `Last eval set run ${formatDateTime(lastEvalAt)}` : "Eval set has not been re-run in this environment."}
          </span>
        </div>
        <DataTable
          rows={metrics}
          rowKey={(m) => m.id}
          columns={[
            {
              key: "metric",
              header: "Metric",
              cellClassName: "whitespace-normal",
              render: (metric) => (
                <div className="flex flex-col gap-0.5" data-demo-scoped={scorecardData.provenance.demoScoped ? "true" : undefined}>
                  <span className="font-medium text-foreground">{displayLabel(metric.label)}</span>
                  <Mono>{displayMetricId(metric.id)}</Mono>
                </div>
              ),
            },
            {
              key: "now",
              header: "Now",
              render: (metric) => <ToneBadge tone={metricTone(metric.status)}>{metric.value}</ToneBadge>,
            },
            {
              key: "target",
              header: "Target",
              render: (metric) => <Mono>{metric.target}</Mono>,
            },
            {
              key: "direction",
              header: "Direction",
              render: (metric) => (metric.direction === "higher-is-better" ? "higher is better" : "lower is better"),
            },
            {
              key: "note",
              header: "What it means here",
              cellClassName: "whitespace-normal",
              render: (metric) => (
                <span className="text-muted-foreground">{displayLabel(metric.note)}</span>
              ),
            },
          ]}
        />
      </section>

      <div className="vp-callout" data-tone="unknown" style={{ marginTop: "calc(var(--spacing) * 4.5)" }}>
        <div className="vp-callout-title">Why this scorecard is not fully green.</div>
        <p className="vp-callout-body">
          {failing.length} metric{failing.length === 1 ? "" : "s"} off target and {warning.length} in warning.
          The table above is the source of truth for this environment; fixing findings, restoring sources, and
          refreshing live proof move these rows independently.
        </p>
        <div style={{ marginTop: "calc(var(--spacing) * 2.5)" }}>
          <ScorecardActionRow onNavigate={onNavigate} disabled />
        </div>
      </div>
    </PageFrame>
  );
}

export function RuntimePage({ onNavigate }: VerificationPageProps) {
  return (
    <MaybeDemo>
      <RuntimePageInner onNavigate={onNavigate} />
    </MaybeDemo>
  );
}

function RuntimeActionRow({
  onRunDoctor,
  onOpenRunbook,
  disabled,
}: {
  onRunDoctor?: () => void;
  onOpenRunbook?: () => void;
  disabled?: boolean;
}) {
  return (
    <PageActions
      secondary={
        <ActionButton
          actionId={RUNTIME_RERUN_DOCTOR_ACTION}
          data-action-id={RUNTIME_RERUN_DOCTOR_ACTION}
          disabled={disabled}
          onClick={onRunDoctor}
        >
          Refresh doctor status
        </ActionButton>
      }
      primary={
        <ActionButton
          actionId={RUNTIME_OPEN_RUNBOOK_ACTION}
          data-action-id={RUNTIME_OPEN_RUNBOOK_ACTION}
          variant="primary"
          disabled={disabled}
          onClick={onOpenRunbook}
        >
          Open rotation requirements
        </ActionButton>
      }
    />
  );
}

function RuntimePageInner({ onNavigate }: VerificationPageProps) {
  const env = useVerificationEnvOptional();
  const environment = env?.environment ?? null;
  const ready = env?.ready ?? true;
  const [lastDoctorAt, setLastDoctorAt] = useState<string | null>(null);
  const [runbookOpen, setRunbookOpen] = useState(false);
  const runtimeApi = useApi(
    verificationCanonicalClients.runtimeGet.operation,
    ready && environment ? { query: { env: environment } } : null,
  );
  const lastFetchedAt = useLastFetchedAt(runtimeApi.data);

  const checks = runtimeApi.data?.checks ?? [];
  const failing = checks.filter((c) => c.status === "fail");
  const warning = checks.filter((c) => c.status === "warn");
  const passing = checks.filter((c) => c.status === "pass");
  const doctorTone = checks.length === 0 ? "unknown" : failing.length > 0 ? "fail" : warning.length > 0 ? "unknown" : "pass";
  const doctorValue =
    checks.length === 0
      ? "Not available"
      : failing.length === 0
      ? warning.length === 0
        ? "All pass"
        : `${warning.length} warn`
      : `${failing.length} fail`;

  if (env && !ready) {
    return (
      <PageFrame page="runtime" title="Runtime & keys" lede={RUNTIME_LEDE}>
        <PagePending title="Runtime not loaded">Loading key custody and doctor checks for the active Environment.</PagePending>
      </PageFrame>
    );
  }

  if (!environment) {
    return (
      <PageFrame page="runtime" title="Runtime & keys" lede={RUNTIME_LEDE}>
        <PagePending title="Runtime not loaded">Runtime doctor and rotation runbook are environment-scoped. Pick an environment to inspect persisted checks.</PagePending>
      </PageFrame>
    );
  }

  if (runtimeApi.error && !runtimeApi.data) {
    return (
      <PageFrame page="runtime" title="Runtime & keys" lede={RUNTIME_LEDE}>
        <PageError title="Runtime failed to load" error={runtimeApi.error} onRetry={() => void runtimeApi.mutate()} />
      </PageFrame>
    );
  }

  if (runtimeApi.isLoading || !runtimeApi.data) {
    return (
      <PageFrame page="runtime" title="Runtime & keys" lede={RUNTIME_LEDE}>
        <PagePending title="Loading runtime">Loading {environment === "demo" ? "Demo" : "Real"} runtime checks.</PagePending>
      </PageFrame>
    );
  }

  const signingKey = runtimeApi.data.signingKey;

  return (
    <PageFrame
      page="runtime"
      title="Runtime & keys"
      lede={RUNTIME_LEDE}
      actions={
        <RuntimeActionRow
          onRunDoctor={() => {
            void runtimeApi.mutate().then(() => {
              setLastDoctorAt(new Date().toISOString());
              setRunbookOpen(false);
            });
          }}
          onOpenRunbook={() => setRunbookOpen(true)}
        />
      }
    >
      <LastFetched at={lastFetchedAt} stale={Boolean(runtimeApi.error && runtimeApi.data)} />
      <StatGrid cols={3}>
        <Stat
          label="Runtime mode"
          value={runtimeApi.data.runtimeMode}
          tone={runtimeApi.data.runtimeMode === "unconfigured" ? "unknown" : "accent"}
          foot={runtimeApi.data.runtimeMode === "unconfigured" ? "No runtime configuration reported" : "Inspect runtime checks before relying on this declaration"}
        />
        <Stat label="Key custody" value={signingKey?.custody ?? "not configured"} tone={signingKey ? "pass" : "unknown"} foot={signingKey ? "Reported by this environment" : "No signer boundary observed"} />
        <Stat
          label="Doctor"
          value={doctorValue}
          tone={doctorTone}
          foot={
            checks.length === 0
              ? lastDoctorAt
                ? `No checks reported · checked ${formatDateTime(lastDoctorAt)}`
                : "No checks reported"
              : lastDoctorAt
              ? `Re-run ${formatDateTime(lastDoctorAt)} — ${passing.length} pass, ${warning.length} warn, ${failing.length} fail`
              : `${checks.length} checks`
          }
        />
      </StatGrid>

      {lastDoctorAt && checks.length > 0 ? (
        <div data-doctor-rerun="true">
          <Callout tone={doctorTone} title="Runtime status refreshed">
            {passing.length} pass, {warning.length} warn, {failing.length} fail. Checked {formatDateTime(lastDoctorAt)}.
          </Callout>
        </div>
      ) : lastDoctorAt ? (
        <div data-doctor-rerun="true">
          <Callout tone="unknown" title="No runtime checks reported">
            Status remains unavailable. Checked {formatDateTime(lastDoctorAt)}.
          </Callout>
        </div>
      ) : null}

      <Section title="Runtime doctor" note="Each check states what it verified, not just that it passed.">
        {checks.length === 0 ? (
          <PagePending title="No runtime checks">Runtime doctor checks land here once the Real runtime reports them.</PagePending>
        ) : (
          <Card className="gap-0 overflow-hidden py-0" data-demo-scoped={runtimeApi.data.provenance.demoScoped ? "true" : undefined}>
            <CardContent className="p-0">
              {checks.map((c) => (
                <RuntimeCheckRow key={c.id} check={c} />
              ))}
            </CardContent>
          </Card>
        )}
      </Section>

      <Section title="Key rotation" note="Preserving old receipts through rotation is a requirement, not a current guarantee.">
        <Card>
          <CardContent>
            <Fields>
              <Field label="Active key">
                {signingKey ? (
                  <>
                    <Mono>{signingKey.id}</Mono> — {signingKey.custody}
                  </>
                ) : (
                  "No signing key configured"
                )}
              </Field>
              <Field label="Policy">
                {signingKey
                  ? `Every ${signingKey.rotationPolicyDays} days. Next rotation ${signingKey.nextRotationAt ? formatDateTime(signingKey.nextRotationAt) : "not scheduled"}.`
                  : "No rotation policy is attached to this environment yet."}
              </Field>
            </Fields>
          </CardContent>
        </Card>
      </Section>

      {runbookOpen ? (
        <Section title="Planned rotation requirements" note="No rotation is executed from this page.">
          <Card data-runbook-open="true">
            <CardContent className="flex flex-col gap-3">
              <ol className="list-decimal space-y-2 pl-5 type-body text-foreground">
                <li>A future implementation must generate the next signing key inside the configured custody boundary.</li>
                <li>It must produce independently verifiable transition evidence before activating the key.</li>
                <li>It must publish trust material outside the exported pack.</li>
                <li>It must retain enough public material to verify older signed envelopes.</li>
              </ol>
              <p className="type-subtext text-muted-foreground">
                Trust-root fingerprints live on{" "}
                <PageLink onNavigate={onNavigate} to="trust">
                  Trust root
                </PageLink>
                . Security questionnaire answers live on{" "}
                <PageLink onNavigate={onNavigate} to="posture">
                  Product design posture
                </PageLink>
                .
              </p>
            </CardContent>
          </Card>
        </Section>
      ) : null}
    </PageFrame>
  );
}

function RuntimeCheckRow({ check }: { check: { id: string; label: string; status: string; detail: string } }) {
  const tone = check.status === "pass" ? "pass" : check.status === "warn" ? "unknown" : "fail";
  return (
    <ListRow
      checkId={check.id}
      leading={<ToneBadge tone={tone}>{check.status}</ToneBadge>}
      title={check.label}
      subtitle={check.detail}
    />
  );
}

const RUNTIME_LEDE =
  "Reported runtime mode, observed checks, and signing-key state. Missing checks or keys remain explicitly unavailable.";
export { ApiPage, API_OPEN_ENDPOINT_ACTION, PosturePage, TrustPage } from "./operate-secondary";
